import {_} from 'lodash';
import * as ko from "knockout";
import {IButton, IRouteDefinitionParams, TBaseOrObservableType, TRouteId} from "../../index";
import {_d, getEadmRole} from "../../util/misc";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {getDisplayNameForRouteData, getObjectFromRouteData} from "../../util/request";
import {nsRoute} from "../../util/route";
import {nsBadge} from "../../util/badge";
import {Permission} from "../../constants";
import {getRouteButton, isRouteDefinition} from "../../constants/RouteDefinitions";
import {global} from "../../appContext";
import {storeObservable} from "../../util/sessionstore";
import {ButtonManager} from "../../util/ButtonManager";
import {ISplitButtonParams} from "../button/splitButton/split-button";
import {supportsPageShellObjectActions} from "../page-shell/PageShellObjectSupport";
import {usesPageShellHeaderActionBar} from "../page-shell/pageShellHeaderActions";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import TBadgeValue = nsBadge.TBadgeValue;
import ButtonOrder = nsRoute.ButtonOrder;
import TBadgeObject = nsBadge.TBadgeObject;
import headingHtml from './heading.html?raw';

export let template = headingHtml;

export interface IParams extends IRouteDefinitionParams {
    cls?: string
    isWaiting?: KnockoutObservable<boolean>
    badge?: TBaseOrObservableType<TBadgeValue>
    badgeError?: KnockoutObservable<TBadgeValue>
    buttonManager?: ButtonManager
    label?: TBaseOrObservableType<string>
    splitButtonParams?: KnockoutObservable<ISplitButtonParams | null> | KnockoutComputed<ISplitButtonParams | null>
}

export class viewModel extends ParsedRouteDataDecorator {
    private readonly badge: KnockoutObservable<TBadgeValue | TBadgeObject | null>;
    private readonly badgeError: KnockoutObservable<TBadgeValue | null>;
    private readonly cls: string | undefined;
    private readonly clsLevel: string;
    private readonly buttonManager: ButtonManager
    private readonly getBadgeValue: (badge: KnockoutObservable<TBadgeValue | TBadgeObject | null>) => TBadgeValue | null;
    private readonly icon: string | undefined;
    private readonly isReady: boolean;
    private readonly isWaiting: KnockoutObservable<boolean>;
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly name: KnockoutObservable<string | null> = ko.observable(null);
    private readonly splitButtonParams: KnockoutObservable<ISplitButtonParams | null>
        | KnockoutComputed<ISplitButtonParams | null> | null;
    protected routeData: any;

    constructor(protected params: IParams = {}) {
        super(params);
        this.isReady = this.__routeDef.showHeading !== false;
        this.icon = this.__routeDef.icon;
        this.cls = params.cls;
        this.clsLevel = 'level-' + this.__routeDef.level;
        this.isWaiting = params.isWaiting ?? params.__requestRouteData?.isRequesting ?? ko.observable(false);
        if ('badge' in params) {
            if (ko.isObservable(params.badge)) {
                this.badge = params.badge as KnockoutObservable<TBadgeValue | TBadgeObject | null>;
            } else {
                this.badge = ko.observable<TBadgeValue | TBadgeObject | null>(params.badge ?? null);
            }
        } else {
            // Follow the active tab so soft-nav updates heading badges without remount.
            const tabManager = params.__tabManager;
            if (tabManager) {
                this.badge = ko.pureComputed(() => {
                    const tabBadge = tabManager.currentTab()?.badge;
                    return tabBadge ? tabBadge() : null;
                }) as KnockoutObservable<TBadgeValue | TBadgeObject | null>;
            } else {
                this.badge = ko.observable<TBadgeValue | TBadgeObject | null>(null);
            }
        }
        if ('badgeError' in params) {
            this.badgeError = ko.isObservable(params.badgeError)
                ? params.badgeError as KnockoutObservable<TBadgeValue | null>
                : ko.observable<TBadgeValue | null>(params.badgeError ?? null)
        } else {
            this.badgeError = ko.observable<TBadgeValue | null>(null)
        }

        this.getBadgeValue = nsBadge.getValue.bind(null, this.badge);

        if (this.__routeDef.fetchRouteDef) {
            if (!this.__routeDef.fetchRouteDef.serviceName) {
                _d.warn('No service name defined for route', this.__routeDef)
            }
            this.__requestRouteData
                ?.request()
                .then((data: any[]) => {
                    this.routeData = getObjectFromRouteData(data, this.__routeDef, this.__routeParams ?? {});
                    this.updateButtons();
                    this.name(getDisplayNameForRouteData(data, this.__routeDef, this.__routeParams ?? {}));
                    this.isWaiting(false);
                })
                .catch((reason: string) => this.isWaiting(false));
        }
        if (params.buttonManager) {
            this.buttonManager = params.buttonManager;
        } else {
            this.buttonManager = new ButtonManager()
         }
        this.splitButtonParams = params.splitButtonParams ?? null;

        // Keep heading error badge in sync with the active tab (soft-nav does not remount heading).
        this.bindActiveTabErrorBadge(params);
        this.updateButtons();
    }

    private activeTabErrorSub: KnockoutSubscription | null = null;

    /** Subscribe to the active tab's error count (and title source) for soft-nav tab switches. */
    private bindActiveTabErrorBadge(params: IParams) {
        if ('badgeError' in params) {
            return;
        }
        const tabManager = params.__tabManager;
        if (!tabManager) {
            if (this.__tab?.errorManager?._getNumErrors) {
                this.kosub.subscribe(this.__tab.errorManager._getNumErrors, num => {
                    this.badgeError(num);
                });
                this.badgeError(this.__tab.errorManager._getNumErrors());
            }
            return;
        }
        this.kosub.subscribe(tabManager.currentTab, (tab) => {
            this.activeTabErrorSub?.dispose();
            this.activeTabErrorSub = null;
            const numErrors = tab?.errorManager?._getNumErrors;
            if (!numErrors) {
                this.badgeError(null);
                return;
            }
            this.activeTabErrorSub = numErrors.subscribe(num => this.badgeError(num));
            this.badgeError(numErrors());
        });
    }

    private getActiveTabErrorTitle(): string | undefined {
        return this.params.__tabManager?.currentTab()?.errorManager.getErrorMessagesString()
            ?? this.__tab?.errorManager.getErrorMessagesString();
    }

    private showBadge(): boolean {
        return this.badge() !== undefined && this.badge() !== null;
    }

    /** Show the actions column when a split button or legacy heading buttons are present. */
    private showHeadingActions = ko.pureComputed((): boolean => {
        if (this.splitButtonParams?.()) {
            return true;
        }
        return this.__showHeadingButtons && this.buttonManager.sorted().length > 0;
    });

    private buttons = ko.pureComputed(() => {
        return _(this.buttonManager.buttons())
            .filter((btn: IButton) => (btn.permissionLevel ?? Permission.None) <= getEadmRole())
            .sortBy((btn: IButton) => btn.order ?? 0)
            .value()
            ;
    })

    private updateButtons() {
        if (window.location.pathname.includes('win.html')) {
            return;
        }
        const useHeaderActionBar = usesPageShellHeaderActionBar(this.__routeDef);
        if ('create' in this.__routeDef && this.__routeDef.create && !useHeaderActionBar) {
            const btn = getRouteButton(this.__routeDef.create, {order: ButtonOrder.CREATE});
            this.addButton(btn);
        }
        if ('createWizard' in this.__routeDef && this.__routeDef.createWizard && !useHeaderActionBar) {
            const btn = getRouteButton(this.__routeDef.createWizard, {order: ButtonOrder.CREATE_WIZARD});
            this.addButton(btn);
        }
        // Object-detail / feature shells surface edit via the tab-row action bar instead.
        if ('edit' in this.__routeDef && this.__routeDef.edit
            && !supportsPageShellObjectActions(this.__routeDef)
            && !useHeaderActionBar) {
            const btn = getRouteButton(this.__routeDef.edit, {order: ButtonOrder.EDIT});
            this.addButton(btn);
        }
        if (this.__routeDef.level == 1) {
            if (this.routeData && this.__routeDef.isSubitem && storeObservable('latest.objects.show.limit')()) {
                const __id = this.routeData.id ?? this.__id
                const isListed = ko.pureComputed(() => global.latestObjectsManager.isListed(__id));
                const isBookmarked = ko.pureComputed(() => global.latestObjectsManager.isBookmarkedById(__id));
            }
        }
        if (!useHeaderActionBar) {
            this.__routeDef.customButtons?.forEach((idOrBtn: TRouteId | IButton) => {
                const isRoute = isRouteDefinition(idOrBtn as TRouteId);
                let btn: IButton;
                if (isRoute) {
                    btn = getRouteButton(idOrBtn as TRouteId);
                } else {
                    btn = idOrBtn as IButton;
                }
                this.addButton(btn);
            });
        }
    }

    private addButton(btn: IButton) {
        this.buttonManager.add(btn);
    }

    override dispose() {
        super.dispose();
        delete this.routeData;
        this.activeTabErrorSub?.dispose();
        this.activeTabErrorSub = null;
        this.kosub.unsuscribeAll();
        this.showHeadingActions.dispose();
    }
}
